import React from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

interface TimeSpinnerProps {
  date?: Date;
  onChange?: (time: string) => void;
  // react-datepicker passes value (string), date (Date), onChange (func), etc.
  [key: string]: any;
}

const TimeSpinner: React.FC<TimeSpinnerProps> = ({ date, onChange }) => {
  // Use current date if none provided
  const currentDate = date || new Date();

  const performChange = (newDate: Date) => {
    if (!onChange) return;
    // react-datepicker's customTimeInput onChange expects a string e.g. "14:30"
    const h = newDate.getHours().toString().padStart(2, "0");
    const m = newDate.getMinutes().toString().padStart(2, "0");
    onChange(`${h}:${m}`);
  };

  const updateTime = (offsetHours: number, offsetMinutes: number) => {
    const newDate = new Date(currentDate);
    newDate.setHours(newDate.getHours() + offsetHours);
    newDate.setMinutes(newDate.getMinutes() + offsetMinutes);
    performChange(newDate);
  };

  const toggleAmPm = () => {
    const newDate = new Date(currentDate);
    const hours = newDate.getHours();
    newDate.setHours(hours >= 12 ? hours - 12 : hours + 12);
    performChange(newDate);
  };

  // Format explicitly
  let hours = currentDate.getHours();
  const isPm = hours >= 12;
  const displayHours = hours % 12 || 12; // Convert 0 to 12
  const displayMinutes = currentDate.getMinutes().toString().padStart(2, "0");

  const ButtonStyle = "w-9 h-9 flex items-center justify-center bg-[#885ABB] text-white rounded-md cursor-pointer hover:bg-[#7748A4] transition-colors shadow-sm";

  return (
    <div className="flex flex-col items-center justify-center p-2 sm:p-4 bg-white rounded-md border-0 gap-2 w-fit mx-auto h-full">
      {/* Up Arrows */}
      <div className="flex items-center gap-2 sm:gap-4">
        <button
          className={ButtonStyle}
          onClick={(e) => { e.preventDefault(); updateTime(1, 0); }}
          type="button"
        >
          <ChevronUp size={20} strokeWidth={3} />
        </button>
        <div className="w-1 sm:w-2" />
        <button
          className={ButtonStyle}
          onClick={(e) => { e.preventDefault(); updateTime(0, 1); }}
          type="button"
        >
          <ChevronUp size={20} strokeWidth={3} />
        </button>
        <div className="w-8 sm:w-10" />
      </div>

      {/* Time Display */}
      <div className="flex items-center gap-2 sm:gap-4 text-[#885ABB] font-bold text-[18px] sm:text-[20px]">
        <div className="w-8 sm:w-9 text-center">{displayHours}</div>
        <div className="w-1 sm:w-2 text-center">:</div>
        <div className="w-8 sm:w-9 text-center">{displayMinutes}</div>
        <button
          className="w-10 h-8 flex items-center justify-center bg-[#885ABB] text-white text-[10px] sm:text-[12px] font-bold rounded cursor-pointer hover:bg-[#7748A4] transition-colors ml-0 shadow-sm"
          onClick={(e) => { e.preventDefault(); toggleAmPm(); }}
          type="button"
        >
          {isPm ? "PM" : "AM"}
        </button>
      </div>

      {/* Down Arrows */}
      <div className="flex items-center gap-2 sm:gap-4">
        <button
          className={ButtonStyle}
          onClick={(e) => { e.preventDefault(); updateTime(-1, 0); }}
          type="button"
        >
          <ChevronDown size={20} strokeWidth={3} />
        </button>
        <div className="w-1 sm:w-2" />
        <button
          className={ButtonStyle}
          onClick={(e) => { e.preventDefault(); updateTime(0, -1); }}
          type="button"
        >
          <ChevronDown size={20} strokeWidth={3} />
        </button>
        <div className="w-8 sm:w-10" />
      </div>
    </div>
  );
};

export default TimeSpinner;
